import React, { useState, useEffect } from 'react';
import { useAppStore } from '../../stores/useAppStore';
import { getAffinityProgress } from '../../stores/useUserStore';
import type { WeatherData } from '../../hooks/useWeather';
import { getTodayDate } from '../../utils/dateUtils';
import { Cloud, CloudRain, CloudSnow, Sun, Zap } from 'lucide-react';
import './Header.css';

interface HeaderProps {
  onToggleMenu?: () => void;
  menuOpen?: boolean;
  idle?: boolean;
  weather?: WeatherData; // useWeatherの二重実行を避けるため親から受け取る
}

const WeatherIcon: React.FC<{ condition: string; size?: number }> = ({ 
  condition, 
  size = 20 
}) => {
  const iconProps = { size, className: 'text-white/80' };
  
  switch (condition) {
    case 'sunny':
      return <Sun {...iconProps} className="text-yellow-300" />;
    case 'cloudy':
      return <Cloud {...iconProps} />;
    case 'rainy':
      return <CloudRain {...iconProps} className="text-blue-300" />;
    case 'snowy':
      return <CloudSnow {...iconProps} className="text-blue-100" />;
    case 'thunder':
      return <Zap {...iconProps} className="text-purple-300" />;
    default:
      return null;
  }
};

const Header: React.FC<HeaderProps> = ({
  onToggleMenu,
  menuOpen = false,
  idle = false,
  weather = { condition: 'unknown' }
}) => {
  const { donuts, affinityLevel, affinityExp, pomodoroLog } = useAppStore();
  const today = getTodayDate();
  const todayWorkMinutes = pomodoroLog[today] || 0;
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showColon, setShowColon] = useState(true);
  const [donutFlash, setDonutFlash] = useState(false);

  // 時刻更新
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
      setShowColon(prev => !prev);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // ドーナツ数が変化したときのアニメーション
  useEffect(() => {
    setDonutFlash(true);
    const timer = setTimeout(() => setDonutFlash(false), 500);
    return () => clearTimeout(timer);
  }, [donuts]);

  // 日付フォーマット（Vue版と同じ形式）
  const monthDay = currentTime.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: '2-digit'
  });

  // 時刻フォーマット（12時間制）
  const hours = currentTime.getHours();
  const minutes = currentTime.getMinutes();
  const amPm = hours >= 12 ? 'PM' : 'AM';
  const hour = String(hours % 12 || 12).padStart(2, '0');
  const minute = String(minutes).padStart(2, '0');

  // 経験値の進捗率を計算（テーブルはuseUserStoreと共通）
  const expProgress = getAffinityProgress(affinityExp, affinityLevel) * 100;

  return (
    <div className="header-box">
      <div className="flex items-center justify-between w-full">
        {/* 時計 */}
        <div className="datetime-inner">
          <div className="date-stack">
            <div className="day-text text-white">{monthDay}</div>
          </div>
          <div className="time-text flex items-baseline gap-1 font-mono">
            <span className="text-white">{hour}</span>
            <span
              className={`transition-opacity duration-500 ease-in-out text-blue-400 ${
                showColon ? 'opacity-100' : 'opacity-30'
              }`}
            >
              :
            </span>
            <span className="text-white">{minute}</span>
            <span className="text-xs ml-1 text-white font-sans">{amPm}</span>
            
            {/* 天気表示 - 時間の真横に配置 */}
            {weather.condition !== 'unknown' && (
              <div className="flex items-center gap-2 ml-3">
                <WeatherIcon condition={weather.condition} />
                {weather.temperature !== undefined && (
                  <span className="text-white/80 text-sm font-medium">
                    {weather.temperature}°C
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ハンバーガーメニュー */}
        {!idle && (
          <div
            className={`hamburger ${menuOpen ? 'open' : ''}`}
            onClick={onToggleMenu}
          >
            <span></span>
          </div>
        )}
      </div>

      {/* ドーナツと好感度 */}
      <div className="flex gap-4 w-full text-white text-sm items-center">
        {/* 今日の作業時間 */}
        <div className="flex items-center">
          <span className="text-white text-sm cursor-pointer hover:opacity-80 transition flex items-center gap-1">
            <img src="/assets/img/fire.svg" alt="work time" className="w-5 h-5 object-contain" />
            <span>{todayWorkMinutes}分</span>
          </span>
        </div>
        
        {/* ドーナツアイコン */}
        <div className="flex items-center gap-1">
          <img src="/assets/img/donut.svg" alt="donut" className="w-5 h-5 object-contain" />
          <span className={donutFlash ? 'animate-pop' : ''}>{donuts}</span>
        </div>

        {/* 好感度レベル */}
        <div className="flex items-center gap-1">
          <img src="/assets/img/heart.svg" alt="heart" className="w-5 h-5 object-contain" />
          <span>Lv.{affinityLevel}</span>
        </div>

        {/* ゲージ（好感度EXP） */}
        <div className="w-[120px] h-1 bg-gray-700 rounded mt-1 overflow-hidden">
          <div
            className="h-full bg-pink-500 transition-all duration-300"
            style={{ width: `${expProgress}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default Header;